// Phase 15 — Cache OfficialRate rows across calculator mounts in the same
// session. 125 rows total across all kinds; one GET → in-memory split per
// kind. Bypasses useTaxonomy because the OfficialRate table is its own
// shape (kind + value + meta as the rate driver, not labels).

import { useEffect, useState } from "react";
import { api } from "../api";
import type { OfficialRate } from "@shared/entities";

const cache: {
  rows: OfficialRate[] | null;
  loading: boolean;
  error: unknown;
  inflight: Promise<OfficialRate[]> | null;
  subscribers: Set<() => void>;
} = {
  rows: null,
  loading: false,
  error: null,
  inflight: null,
  subscribers: new Set(),
};

function notify() {
  for (const cb of cache.subscribers) cb();
}

async function fetchOnce() {
  if (cache.rows) return cache.rows;
  if (cache.inflight) return cache.inflight;
  cache.loading = true;
  cache.error = null;
  cache.inflight = (async () => {
    try {
      const rows = await api.get("/official-rates");
      cache.rows = Array.isArray(rows) ? rows : [];
      return cache.rows;
    } catch (e) {
      cache.error = e;
      cache.rows = [];
      return cache.rows;
    } finally {
      cache.loading = false;
      cache.inflight = null;
      notify();
    }
  })();
  return cache.inflight;
}

export function useOfficialRates() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const cb = () => setTick((t) => t + 1);
    cache.subscribers.add(cb);
    if (!cache.rows && !cache.inflight) fetchOnce();
    return () => {
      cache.subscribers.delete(cb);
    };
  }, []);
  return {
    rates: cache.rows || [],
    loading: cache.loading,
    error: cache.error,
  };
}

export function ratesByKind(rows: OfficialRate[] | null | undefined, kind: string) {
  return (rows || []).filter((r) => r.kind === kind);
}

export function invalidateOfficialRates() {
  cache.rows = null;
  cache.error = null;
  notify();
}
